import { addLead } from './leads';
import type { Lead, Contact, LeadSource } from '../types';
import type { ApartmentFromPlaces } from '../types/apartments';
import { googleMapsService } from '../services/googleMaps';

// Modern Google Places API types using the new Place class
interface GooglePlace {
  place_id: string;
  displayName: string;
  formattedAddress: string;
  location: {
    lat: number;
    lng: number;
  };
  types: string[];
  rating?: number;
  userRatingsTotal?: number;
  photos?: Array<{
    name: string;
    widthPx: number;
    heightPx: number;
    authorAttributions?: Array<{
      displayName: string;
      uri: string;
      photoURI: string;
    }>;
  }>;
  internationalPhoneNumber?: string;
  nationalPhoneNumber?: string;
  websiteURI?: string;
}

// Extract city and country from formatted address
function extractLocationInfo(formattedAddress: string): { city: string; country: string } {
  const parts = formattedAddress.split(',').map(part => part.trim());
  const city = parts[parts.length - 3] || parts[parts.length - 2] || 'Unknown City';
  const country = parts[parts.length - 1] || 'Unknown Country';
  return { city, country };
}

// Initialize Google Maps using the centralized service
async function initializeGoogleMaps(): Promise<void> {
  if (googleMapsService.isInitialized()) return;
  
  try {
    await googleMapsService.initialize();
  } catch (error) {
    console.error('[API] Error initializing Google Maps:', error);
    throw error;
  }
}

// Search for apartments using modern searchByText method
async function searchApartmentsByText(
  location: { lat: number; lng: number },
  searchTerms: string[] = ['Condominio', 'Departamento', 'Edificio', 'Apartamento', 'Apartment']
): Promise<GooglePlace[]> {
  let allPlaces: GooglePlace[] = [];

  // Try specific search terms first
  for (const searchTerm of searchTerms) {
    try {
      const places = await searchByText(searchTerm, location);
      allPlaces = [...allPlaces, ...places];
    } catch (error) {
      console.warn(`[API] Error searching for "${searchTerm}":`, error);
    }
  }

  // If no results with specific terms, try a broader search
  if (allPlaces.length === 0) {
    try {
      const places = await searchByText('apartment', location);
      allPlaces = [...allPlaces, ...places];
    } catch (error) {
      console.warn('[API] Error with broader search:', error);
    }
  }

  // Remove duplicates based on place_id
  const uniquePlaces = allPlaces.filter((place, index, self) => 
    index === self.findIndex(p => p.place_id === place.place_id)
  );

  return uniquePlaces;
}

// Modern searchByText method using the new Place.searchByText API
async function searchByText(
  query: string,
  location: { lat: number; lng: number }
): Promise<GooglePlace[]> {
  if (!googleMapsService.isInitialized()) {
    throw new Error('Google Maps service not initialized');
  }

  try {
    // Use the modern searchPlacesByText method from the service
    const places = await googleMapsService.searchPlacesByText(query, location);

    console.log('Raw API response:', places);

    if (places && places.length > 0) {
      const googlePlaces: GooglePlace[] = [];

      for (const place of places) {
        try {
          // Handle lat/lng as functions or numbers
          const lat = typeof place.location?.lat === 'function' ? place.location.lat() : place.location?.lat || 0;
          const lng = typeof place.location?.lng === 'function' ? place.location.lng() : place.location?.lng || 0;
          
          const googlePlace: GooglePlace = {
            place_id: place.id,
            displayName: place.displayName || '',
            formattedAddress: place.formattedAddress || '',
            location: {
              lat: lat,
              lng: lng,
            },
            types: place.types || [],
            rating: place.rating,
            userRatingsTotal: place.userRatingsTotal || 0,
            photos: place.photos || [],
            internationalPhoneNumber: place.internationalPhoneNumber,
            nationalPhoneNumber: place.nationalPhoneNumber,
            websiteURI: place.websiteURI,
          };

          googlePlaces.push(googlePlace);
        } catch (error) {
          console.warn(`[API] Error processing place ${place.id}:`, error);
        }
      }

      return googlePlaces;
    } else {
      return [];
    }
  } catch (error) {
    console.error(`[API] Error in searchByText for "${query}":`, error);
    return [];
  }
}

// Get nearby apartments from Google Places API
export async function getNearbyApartments(
  userLocation: { lat: number; lng: number }, 
  searchTerms?: string[]
): Promise<ApartmentFromPlaces[]> {
  try {
    // Initialize Google Maps if not already done
    await initializeGoogleMaps();
    
    // Search for apartments with specified search terms
    const places = await searchApartmentsByText(userLocation, searchTerms);
    
    // Transform places directly to apartments format
    const apartments = places.map((place) => {
      const { city, country } = extractLocationInfo(place.formattedAddress);
      
      const apartment: ApartmentFromPlaces = {
        id: place.place_id,
        name: place.displayName,
        address: place.formattedAddress,
        city,
        country,
        latitude: Number(place.location.lat),
        longitude: Number(place.location.lng),
        rating: place.rating,
        totalRatings: place.userRatingsTotal,
        phone: place.internationalPhoneNumber,
        nationalPhone: place.nationalPhoneNumber,
        website: place.websiteURI,
        photos: place.photos || [],
        placeId: place.place_id,
      };

      return apartment;
    });

    // Sort by rating (highest first), then by name
    const sortedApartments = apartments.sort((a, b) => {
      if (a.rating && b.rating && a.rating !== b.rating) {
        return b.rating - a.rating;
      }
      return a.name.localeCompare(b.name);
    });

    return sortedApartments;
  } catch (error) {
    console.error('[API] Error in getNearbyApartments:', error);
    return [];
  }
}

// Convert apartment from Google Places to Lead
export async function convertApartmentToLead(apartment: ApartmentFromPlaces): Promise<string> {
  console.log('[API] convertApartmentToLead called', { apartment });
  
  try {
    const leadData: Omit<Lead, 'id'> = {
      name: apartment.name,
      status: 'New',
      source: 'Google Places' as LeadSource,
      contacts: [
        {
          name: apartment.name,
          position: 'Property Manager',
          email: '', // Would need to be filled manually
          phone: apartment.phone || '',
          type: 'Primary' as Contact['type'],
          value: apartment.phone || '',
        }
      ],
      address: apartment.address,
      city: apartment.city,
      pincode: '', // Would need to be extracted or filled manually
      country: apartment.country,
      description: `Discovered via Google Places API. Rating: ${apartment.rating || 'N/A'}/5 (${apartment.totalRatings || 0} reviews).`,
      notes: `Place ID: ${apartment.placeId}\nWebsite: ${apartment.website || 'N/A'}\nPhotos: ${apartment.photos?.length || 0} available\nGoogle Maps: https://www.google.com/maps/place/?q=place_id:${apartment.placeId}`,
      created_at: new Date(),
      updated_at: new Date(),
      activities: [],
    };

    const docRef = await addLead(leadData);
    console.log('[API] Successfully converted apartment to lead:', docRef.id);
    return docRef.id;
  } catch (error) {
    console.error('[API] Error converting apartment to lead:', error);
    throw error;
  }
}

// Get apartment by ID (for compatibility)
export async function getApartmentById(id: string): Promise<ApartmentFromPlaces | null> {
  console.log('[API] getApartmentById called', { id });
  
  try {
    // This would typically fetch from Google Places API using place_id
    // For now, return null since we're not using mock data
    return null;
  } catch (error) {
    console.error('[API] Error in getApartmentById:', error);
    return null;
  }
} 